<template>
    <div class="elearning-package-activity">
        <portal to="activity-info-elearning">
            <div class="tw:flex tw:items-center tw:divide-x tw:divide-[var(--primary-color)]">
                <div class="tw:flex tw:items-center tw:pr-1">
                    <e-icon
                        icon="redo"
                        :size="$isMobile || $isTablet ? '' : 'is-small'"
                        custom-class="has-text-primary"
                    />
                    <p class="has-text-subtle">Attempts Allowed: {{ attemptsAllowedLabel }}</p>
                </div>
                <div class="tw:flex tw:items-center tw:pr-1">
                    <e-icon
                        icon="award"
                        :size="$isMobile || $isTablet ? '' : 'is-small'"
                        custom-class="has-text-primary"
                    />
                    <p class="has-text-subtle">Mastery Score: {{ masteryScoreLabel }}</p>
                </div>
            </div>
        </portal>

        <div class="columns">
            <div class="column">
                <e-field>
                    <display-content :content="activityContent.content || ''" />
                </e-field>
            </div>
        </div>

        <div v-if="loadingPackage" class="columns">
            <div class="column">
                <p class="has-text-subtle">Loading eLearning package…</p>
            </div>
        </div>

        <template v-else-if="linkedPackage">
            <div
                v-if="packageStatusNotice"
                class="package-status-notice"
                :class="`is-${packageStatusNotice.variant}`"
            >
                <e-icon :icon="packageStatusNotice.icon" class="package-status-notice__icon tw:text-lg" />
                <div class="package-status-notice__content">
                    <p class="package-status-notice__title tw:text-base">{{ packageStatusNotice.title }}</p>
                    <p class="package-status-notice__body tw:text-base">
                        {{ packageStatusNotice.bodyPrefix }}
                        {{ packageStatusNotice.statusLabel }}
                        {{ packageStatusNotice.bodySuffix }}
                    </p>
                </div>
            </div>

            <div v-if="showPackageCard" class="elearning-package-card mb-5">
                <div class="elearning-package-card__body">
                    <div class="elearning-package-card__icon" aria-hidden="true">
                        <i class="pi pi-box tw:text-4xl" />
                    </div>

                    <div class="elearning-package-card__details">
                        <div class="elearning-package-card__title-row">
                            <h3 class="elearning-package-card__title tw:text-base">
                                {{ linkedPackage.title || 'Untitled Package' }}
                            </h3>
                            <span v-if="standardLabel" class="elearning-package-card__badge elearning-package-card__badge--standard tw:text-base">
                                {{ standardLabel }}
                            </span>
                            <span v-if="versionLabel" class="elearning-package-card__badge elearning-package-card__badge--version tw:text-base">
                                {{ versionLabel }}
                            </span>
                        </div>

                        <p v-if="linkedPackage.description" class="elearning-package-card__description tw:text-base">
                            {{ linkedPackage.description }}
                        </p>

                        <div
                            v-if="isInstructorSurface && packagePresentation.authoring_tool"
                            class="elearning-package-card__meta-item tw:text-base"
                        >
                            <e-icon icon="wrench" class="elearning-package-card__meta-icon" />
                            <span class="elearning-package-card__meta-label tw:text-base tw:mt-1">Authoring tool</span>
                            <span class="elearning-package-card__meta-value tw:text-base tw:mt-1">{{
                                packagePresentation.authoring_tool
                            }}</span>
                        </div>
                        <div
                            v-if="packagePresentation.estimated_duration"
                            class="elearning-package-card__meta-item"
                        >
                            <e-icon icon="clock" class="elearning-package-card__meta-icon" />
                            <span class="elearning-package-card__meta-label tw:text-base tw:mt-1">Est. duration</span>
                            <span class="elearning-package-card__meta-value tw:text-base tw:mt-1">{{
                                packagePresentation.estimated_duration
                            }}</span>
                        </div>
                        <div
                            v-if="packagePresentation.mastery_score != null"
                            class="elearning-package-card__meta-item"
                        >
                            <e-icon icon="check-circle" class="elearning-package-card__meta-icon" />
                            <span class="elearning-package-card__meta-label tw:text-base tw:mt-1">Mastery score</span>
                            <span class="elearning-package-card__meta-value tw:text-base tw:mt-1">{{ packagePresentation.mastery_score }}%</span>
                        </div>
                        <div v-if="scoCount != null" class="elearning-package-card__sco tw:text-base">
                            <i class="pi pi-bars tw:text-base" aria-hidden="true" />
                            <span>Activities (SCOs) {{ scoCount }}</span>
                        </div>
                    </div>
                </div>

                <div class="elearning-package-card__actions">
                    <e-button type="button" class="elearning-package-card__btn elearning-package-card__btn--launch tw:text-base" @click="launchPackage">
                        <i class="pi pi-play tw:text-base" aria-hidden="true" />
                        Launch
                    </e-button>
                </div>
            </div>
        </template>

        <div v-else class="columns">
            <div class="column">
                <p class="has-text-subtle">
                    {{ packageLoadError || 'No eLearning package linked to this activity.' }}
                </p>
            </div>
        </div>

        <div v-if="isLate && !completed && !userHasAuthorizedAttempt" class="columns">
            <div class="column">
                <late-notice :can-submit-late="canSubmitLate" />
            </div>
        </div>

        <attempt-authorization-notice
            v-if="hasAnyAttemptAuthorizations"
            :attempt-authorizations="userUnusedAuthorizedAttempts"
        />

        <scorm-student-drawer
            :active="showStudentDrawer"
            :pkg="previewPackage"
            :activity-id="activityId"
            :course-id="course ? course.id : $route.params.courseId"
            hide-runtime
            remember-drawer-state
            @close="onStudentDrawerClose"
            @progress-synced="onScormProgressSynced"
        />

        <activity-completion-action-row class="tw:mt-3">
            <e-button
                v-if="userHasAuthorizedAttempt || (!completed && !readOnly)"
                :loading="isLoading"
                :disabled="disabled || isPackageUnavailableForLearners"
                type="is-primary"
                @click="submit"
                @keyup="submit"
            >
                Mark As Complete
            </e-button>
        </activity-completion-action-row>

        <div v-if="!readOnly" class="columns mt-3">
            <div class="column">
                <div class="elearning-package-submissions">
                    <h2 class="m-b-none tw:text-[var(--p-text-color)]!">My Submissions</h2>

                    <div v-if="submissionsLoading" class="elearning-package-submissions__loading">
                        <e-icon icon="spinner" spin />
                    </div>

                    <div v-else-if="hasSubmissionData" class="elearning-package-submissions__card">
                        <div class="elearning-package-submissions__row">
                            <span class="elearning-package-submissions__label">Status</span>
                            <e-tag :type="lessonStatusTagType(currentSubmission.lesson_status)">
                                {{ formatLessonStatus(currentSubmission.lesson_status) }}
                            </e-tag>
                        </div>
                        <div class="elearning-package-submissions__row">
                            <span class="elearning-package-submissions__label">Score</span>
                            <span class="elearning-package-submissions__value">{{
                                formatScore(currentSubmission.score_raw)
                            }}</span>
                        </div>
                        <div class="elearning-package-submissions__row">
                            <span class="elearning-package-submissions__label">Session time</span>
                            <span class="elearning-package-submissions__value">{{
                                formatSessionTime(currentSubmission.session_time)
                            }}</span>
                        </div>
                    </div>

                    <p v-else class="has-text-subtle elearning-package-submissions__empty">
                        No submissions to display.
                    </p>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <attachments :media="media" :namespace="namespace" :uuid="uuid" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import Attachments from '@/components/activity-types/course-activity-types/activities/Attachments.vue';
import LateNotice from '@/components/activity-types/course-activity-types/LateNotice.vue';
import AttemptAuthorizationNotice from '@/components/attempt-authorizations/AttemptAuthorizationNotice.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import ScormStudentDrawer from '@/components/elearning/ScormStudentDrawer.vue';
import AttemptAuthorizationMixin from '@/mixins/AttemptAuthorization.mixin.js';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';
import IsLateMixin from '@/mixins/IsLate.mixin.js';
import ActivitySubmission from '@/models/ActivitySubmission.js';
import axios from '@/plugins/axios.js';
import {
    formatElearningLessonStatus,
    formatPackageVersion,
    formatScormSessionTime,
    formatStandardLabel,
    getPackageScoCount,
    hasElearningSubmissionData,
    normalizeElearningSubmission,
    presentationFromPackage,
    resolvePackageStatus,
} from '@/utilities/formatElearningPackageMetadata.js';

const PACKAGE_STATUS_NOTICES = {
    archived: {
        variant: 'archived',
        icon: 'archive',
        title: 'Package has been Archived',
        bodyPrefix: 'This package has been ',
        statusLabel: 'Archived',
        bodySuffix:
            ' in the Content Library. Existing learner attempts continue to work. Consider replacing this activity with an active package.',
    },
    draft: {
        variant: 'draft',
        icon: 'exclamation-triangle',
        title: 'Package is in Draft',
        bodyPrefix: 'This package is still in ',
        statusLabel: 'Draft',
        bodySuffix:
            ' status in the Content Library. It is not available for learners until its status becomes Ready. Consider replacing this activity with a ready package.',
    },
};

export default {
    name: 'ElearningPackageActivity',

    components: {
        DisplayContent,
        ScormStudentDrawer,
        ActivityCompletionActionRow,
        Attachments,
        LateNotice,
        AttemptAuthorizationNotice,
    },

    mixins: [IsLateMixin, CourseActivityMixin, AttemptAuthorizationMixin],

    data() {
        return {
            showStudentDrawer: false,
            previewPackage: null,
            isLoading: false,
            loadedPackage: null,
            loadingPackage: false,
            packageLoadError: null,
            elearningSubmissions: [],
        };
    },

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        elearningPackageId() {
            return (
                this.activityContent?.elearning_package_id ??
                this.activityContent?.package_id ??
                this.activityContent?.packageId ??
                null
            );
        },

        linkedPackage() {
            return (
                this.loadedPackage ??
                this.activityContent?.elearning_package ??
                this.activityContent?.linked_package ??
                null
            );
        },

        packagePresentation() {
            return presentationFromPackage(this.linkedPackage);
        },

        standardLabel() {
            if (!this.linkedPackage) {
                return null;
            }

            const standard =
                this.linkedPackage.standard ||
                this.linkedPackage.latest_version?.standard ||
                this.linkedPackage.latestVersion?.standard;

            return formatStandardLabel(standard);
        },

        versionLabel() {
            const version = formatPackageVersion(this.linkedPackage);
            if (!version) {
                return null;
            }

            return String(version).startsWith('v') ? version : `v${version}`;
        },

        scoCount() {
            return getPackageScoCount(this.linkedPackage);
        },

        linkedPackageStatus() {
            return resolvePackageStatus(this.linkedPackage);
        },

        packageStatusNotice() {
            if (!this.linkedPackage) {
                return null;
            }

            return PACKAGE_STATUS_NOTICES[this.linkedPackageStatus] ?? null;
        },

        showPackageCard() {
            if (!this.linkedPackage) {
                return false;
            }

            if (this.isInstructorSurface) {
                return true;
            }

            return this.linkedPackageStatus === 'ready';
        },

        isPackageUnavailableForLearners() {
            const status = this.linkedPackageStatus;

            return status === 'draft' || status === 'archived';
        },

        isInstructorSurface() {
            return this.$gate.allows('viewOther', ['ActivitySubmission', this.course]);
        },

        attemptsAllowedLabel() {
            const attempts = this.activity?.activity_settings?.max_attempts_allowed;

            if (attempts === 0) {
                return 'Unlimited';
            }

            if (attempts == null) {
                return 'N/A';
            }

            return String(attempts);
        },

        masteryScoreLabel() {
            const settingsScore = this.activity?.activity_settings?.minimum_passing_score;

            if (settingsScore != null && settingsScore !== '') {
                return `${settingsScore}%`;
            }

            const packageScore = this.packagePresentation.mastery_score;

            if (packageScore != null) {
                return `${packageScore}%`;
            }

            return 'N/A';
        },

        normalizedSubmissions() {
            const merged = new Map();

            const addSubmission = (submission, fallbackKey) => {
                const normalized = normalizeElearningSubmission(submission);

                if (!hasElearningSubmissionData(normalized)) {
                    return;
                }

                const key = normalized.id ?? fallbackKey;
                const existing = merged.get(key);
                merged.set(key, existing ? { ...existing, ...normalized } : normalized);
            };

            this.elearningSubmissions.forEach((submission, index) => {
                addSubmission(submission, `elearning-api-${index}`);
            });

            this.userSubmissions.forEach((submission, index) => {
                addSubmission(submission, `store-${submission.id ?? index}`);
            });

            return Array.from(merged.values()).sort(
                (left, right) => new Date(right.created_at ?? 0) - new Date(left.created_at ?? 0)
            );
        },

        hasSubmissionData() {
            return this.normalizedSubmissions.length > 0;
        },

        currentSubmission() {
            return this.normalizedSubmissions[0] ?? null;
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.course ? this.course.id : this.$route.params.courseId,
                activity_id: this.activityId,
                activity_type: this.get('activity_type'),
            };
        },
    },

    watch: {
        elearningPackageId: {
            immediate: true,
            handler(packageId) {
                this.loadLinkedPackage(packageId);
            },
        },

        activityId: {
            immediate: true,
            handler() {
                if (!this.readOnly) {
                    this.loadSubmissionData();
                }
            },
        },
    },

    created() {
        if (this.readOnly) {
            this.submissionsLoading = false;
        }
    },

    methods: {
        async loadLinkedPackage(packageId) {
            if (!packageId) {
                this.loadedPackage = null;
                this.packageLoadError = null;
                return;
            }

            if (this.loadedPackage?.id === packageId) {
                return;
            }

            this.loadingPackage = true;
            this.packageLoadError = null;

            try {
                const { data } = await axios.get(`/api/elearning-packages/${packageId}`);
                this.loadedPackage = data.data ?? data;
            } catch {
                this.loadedPackage = null;
                this.packageLoadError = 'Unable to load the linked eLearning package.';
            } finally {
                this.loadingPackage = false;
            }
        },

        async loadSubmissionData() {
            this.submissionsLoading = true;

            try {
                await Promise.all([this.fetchAuthUserSubmissions(), this.fetchElearningSubmissions()]);
            } finally {
                this.submissionsLoading = false;
            }
        },

        async fetchAuthUserSubmissions() {
            const filters = {
                activityId: this.activityId,
                createdBy: this.$store.getters['Auth/userId'],
            };

            const data = await this.$store.dispatch('ActivitySubmissions/fetch', filters);
            await ActivitySubmission.insert({ data });
        },

        async fetchElearningSubmissions() {
            if (!this.activityId) {
                this.elearningSubmissions = [];
                return;
            }

            try {
                const { data } = await axios.get(`/api/activities/${this.activityId}/elearning-package/submissions`);
                this.elearningSubmissions = data.data ?? [];
            } catch {
                this.elearningSubmissions = [];
            }
        },

        launchPackage() {
            if (!this.linkedPackage) return;

            // Learners cannot launch draft/archived packages (card is hidden); block as a safeguard.
            if (!this.isInstructorSurface && this.isPackageUnavailableForLearners) {
                return;
            }

            const pkg = this.linkedPackage;
            const version =
                pkg.version ||
                pkg.version_label ||
                pkg.latest_version?.version_label ||
                pkg.latest_version?.version ||
                pkg.latestVersion?.version_label ||
                pkg.latestVersion?.version ||
                null;

            this.previewPackage = {
                ...pkg,
                version: pkg.version || pkg.version_label || version,
            };
            this.showStudentDrawer = true;
        },

        async onStudentDrawerClose() {
            this.showStudentDrawer = false;
            await this.loadSubmissionData();
        },

        async onScormProgressSynced() {
            await this.syncCompletionFromLatestSubmission();
            await this.loadSubmissionData();
        },

        async syncCompletionFromLatestSubmission() {
            await this.fetchAuthUserSubmissions();

            const latestSubmission = this.latestUserSubmission;

            if (latestSubmission?.marked_completed_at) {
                this.$store.commit(`Activities/${this.uuid}/EDIT_FIELD`, {
                    field: 'completed',
                    value: latestSubmission.marked_completed_at,
                });
            }
        },

        async submit() {
            if (this.isPackageUnavailableForLearners) {
                return;
            }

            this.isLoading = true;

            try {
                await this.$store.dispatch(`${this.namespace}/completeActivity`, this.payload);

                this.$store.commit(`Activities/${this.uuid}/EDIT_FIELD`, {
                    field: 'completed',
                    value: this.$moment.utc().format('YYYY-MM-DD HH:mm:ss'),
                });
            } catch (error) {
                this.$handleResponseError(error);
            }

            this.isLoading = false;
        },

        formatLessonStatus(status) {
            return formatElearningLessonStatus(status) ?? 'Not attempted';
        },

        lessonStatusTagType(status) {
            const normalized = String(status ?? '').toLowerCase();

            if (['passed', 'completed'].includes(normalized)) {
                return 'is-success';
            }

            if (['failed'].includes(normalized)) {
                return 'is-danger';
            }

            if (['incomplete', 'browsed'].includes(normalized)) {
                return 'is-warning';
            }

            return 'is-light';
        },

        formatScore(score) {
            if (score == null || score === '') {
                return '—';
            }

            return `${score}/100`;
        },

        formatSessionTime(value) {
            return formatScormSessionTime(value) ?? '—';
        },
    },
};
</script>

<style scoped lang="scss">
@import '~assets/scss/toolbox.scss';

.elearning-package-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    padding: 1.25rem;
}

.elearning-package-card__body {
    align-items: flex-start;
    display: flex;
    gap: 0.875rem;
}

.elearning-package-card__icon {
    align-items: center;
    background: #eef2f6;
    border-radius: 8px;
    color: #111827;
    display: flex;
    flex-shrink: 0;
    height: 5rem;
    justify-content: center;
    width: 5rem;
}

.elearning-package-card__details {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.3rem;
    min-width: 0;
}

.elearning-package-card__title-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.elearning-package-card__title {
    color: #111827;
    font-weight: 700;
    line-height: 1.3;
    margin: 0;
}

.elearning-package-card__badge {
    border-radius: 9999px;
    display: inline-flex;
    font-weight: 600;
    line-height: 1.25;
    padding: 0.2rem 0.6rem;
}

.elearning-package-card__badge--standard {
    background: #dbeafe;
    color: #1d4ed8;
}

.elearning-package-card__badge--version {
    background: #f3f4f6;
    color: #4b5563;
}

.elearning-package-card__description {
    color: #4b5563;
    line-height: 1.4;
    margin: 0;
}

.elearning-package-card__sco {
    align-items: center;
    color: #6b7280;
    display: inline-flex;
    gap: 0.4rem;
    margin-top: 0.1rem;

    .pi {
        color: #6b7280;
    }
}

.elearning-package-card__actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    margin-top: 1.1rem;
}

.elearning-package-card__btn {
    align-items: center;
    border-radius: 8px;
    border-style: solid;
    border-width: 1px;
    cursor: pointer;
    display: inline-flex;
    font-weight: 600;
    gap: 0.4rem;
    line-height: 1.2;
    padding: 0.55rem 0.9rem;
    transition:
        background-color 0.15s ease,
        border-color 0.15s ease,
        opacity 0.15s ease;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
}

.elearning-package-card__btn--launch {
    background: var(--p-primary-color, #2563eb);
    border-color: var(--p-primary-color, #2563eb);
    color: #fff;

    &:hover:not(:disabled) {
        filter: brightness(0.95);
    }
}

.package-status-notice {
    align-items: flex-start;
    border-radius: 8px;
    border: 1px solid;
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1rem;
    padding: 0.875rem 1rem;
}

.package-status-notice__icon {
    flex-shrink: 0;
    margin-top: 0.15rem;
}

.package-status-notice__content {
    flex: 1;
    min-width: 0;
}

.package-status-notice__title {
    font-weight: 700;
    margin: 0 0 0.35rem;
}

.package-status-notice__body {
    line-height: 1.5;
    margin: 0;
}

.package-status-notice.is-archived {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #334155;
}

.package-status-notice.is-archived .package-status-notice__icon {
    color: #64748b;
}

.package-status-notice.is-draft {
    background: #fffbeb;
    border-color: #fcd34d;
    color: #92400e;
}

.package-status-notice.is-draft .package-status-notice__icon {
    color: #d97706;
}

.elearning-package-submissions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.elearning-package-submissions__loading {
    color: $grey;
    padding: 0.5rem 0;
}

.elearning-package-submissions__card {
    background: $white;
    border: 1px solid $grey-lighter;
    border-radius: 8px;
    display: grid;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
}

.elearning-package-submissions__row {
    align-items: center;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
}

.elearning-package-submissions__label {
    color: $grey;
    font-weight: 600;
}

.elearning-package-submissions__value {
    color: $dark;
    font-weight: 600;
}

.elearning-package-submissions__empty {
    margin: 0;
}

.elearning-package-card__meta-item {
    align-items: center;
    display: inline-flex;
    gap: 0.5rem;
    color: #6b7280;
}

.elearning-package-card__meta-icon {
    color: #6b7280;
    flex-shrink: 0;
}

.elearning-package-card__meta-label {
    color: #4b5563;
    font-weight: 600;
}

.elearning-package-card__meta-value {
    color: #111827;
    font-weight: 600;
}
</style>
